/**
 * aceptacionPropia — marca de "acción propia" para que el banner global de
 * asignación entrante (AsignacionEntranteBanner) no confunda un "Tomar
 * consulta"/"Atender" iniciado por el propio médico con una asignación
 * sorpresa (auto-asignación mientras estaba en otra pantalla).
 *
 * Bug real: `medicoEntrarSala`/`aceptarConsulta` escriben `medicos/{uid}.
 * consultaActiva` en el MISMO write que toma la consulta; el banner (montado
 * globalmente, independiente de qué página hizo la acción) solo ve el
 * RESULTADO en Firestore y no puede distinguir la causa — así que sonaba el
 * timbre de "llamada entrante" incluso cuando el médico mismo acababa de
 * tomar el paciente.
 *
 * Un Set a nivel de módulo alcanza: todo corre en la MISMA pestaña/sesión de
 * un único médico, sin necesidad de Context/props-drilling entre páginas de
 * cola y el banner montado en App.tsx.
 */
const marcados = new Set<string>();

// La marca se comparte ENTRE PESTAÑAS: el Set es por-pestaña, así que si el
// médico tiene el portal abierto en dos pestañas y toma un paciente en una,
// la otra veía el mismo write de consultaActiva sin la marca y timbraba ~20s
// como si fuera una asignación sorpresa.
const canal = typeof BroadcastChannel !== 'undefined'
  ? new BroadcastChannel('minsa-aceptacion-propia')
  : null;
canal?.addEventListener('message', (ev: MessageEvent) => {
  const id = (ev.data as { consultaId?: string } | null)?.consultaId;
  if (typeof id === 'string' && id) {
    marcados.add(id);
    setTimeout(() => marcados.delete(id), 15_000);
  }
});

/** Llamar INMEDIATAMENTE ANTES de iniciar la acción que tomará la consulta
 *  (aceptarConsulta/medicoEntrarSala vía Atender, o el Aceptar del propio
 *  banner) para que el timbre no suene por esta asignación. */
export function marcarAceptacionPropia(consultaId: string): void {
  marcados.add(consultaId);
  canal?.postMessage({ consultaId });
  // Auto-expira: si por lo que sea el banner nunca la consume (p. ej. la
  // llamada falla y no llega a escribir consultaActiva), no debe quedar
  // bloqueando el timbre de una asignación genuinamente nueva más adelante.
  setTimeout(() => marcados.delete(consultaId), 15_000);
}

/** Consumir (una sola vez) la marca — true si esta consulta fue tomada por
 *  el propio médico y el timbre debe omitirse. */
export function consumirAceptacionPropia(consultaId: string): boolean {
  if (!marcados.has(consultaId)) return false;
  marcados.delete(consultaId);
  return true;
}
